import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import Styles from './DIYQuiz.module.scss';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import {
    Pagination,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    Collapse,
    Skeleton
} from '@mui/material';
import { AddCircle } from '@mui/icons-material';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import axios from 'axios';
import { ViewContext } from 'components/lib';
import SearchBar from './SearchBar';

const QuizCard = ({ quiz, onSave, onCancel, isEditing, onEdit, onToggleDescription, expandedId, isAnyCardEditing }) => {
    const navigate = useNavigate();
    const [editData, setEditData] = useState({ name: quiz.name, description: quiz.description });
    const [errors, setErrors] = useState({ name: '', description: '' });
    const descriptionRef = useRef(null);

    useEffect(() => {
        setEditData({ name: quiz.name, description: quiz.description });
    }, [quiz, isEditing]);

    const isExpanded = expandedId === quiz.id;

    const handleToggleDescription = (e) => {
        e.stopPropagation();
        if (!isAnyCardEditing) {
            onToggleDescription(isExpanded ? null : quiz.id);
        }
    };

    const validateFields = () => {
        const newErrors = {
            name: editData.name.trim() === '' ? 'Quiz name is required' : '',
            description: editData.description.trim() === '' ? 'Description is required' : ''
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };

    const handleSave = (e) => {
        e.stopPropagation();
        if (validateFields()) {
            onSave({ ...quiz, ...editData });
        }
    };

    const handleInputChange = (field) => (e) => {
        e.stopPropagation();
        setEditData(prev => ({ ...prev, [field]: e.target.value }));

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleCardClick = () => {
        // Only navigate if no card is being edited
        if (!isAnyCardEditing && !isEditing) {
            navigate(`/diy-quiz/${quiz.id}/assessments`);
        }
    };

    return (
        <div className={Styles.quizCardWrapper}>
            <div
                className={`${Styles.quizCard} ${isAnyCardEditing && !isEditing ? Styles.disabled : ''} ${isExpanded ? Styles.expanded : ''}`}
                onClick={handleCardClick}
            >
                <div className={Styles.mainContent}>
                    <div className={Styles.quizNameContainer}>
                        {isEditing ? (
                            <TextField
                                value={editData.name}
                                onChange={handleInputChange('name')}
                                onKeyDown={(e) => e.key === 'Escape' && onCancel(quiz)}
                                placeholder="Enter quiz name"
                                size="small"
                                autoFocus
                                error={!!errors.name}
                                helperText={errors.name}
                                sx={{
                                    width: '300px',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white'
                                    }
                                }}
                            />
                        ) : (
                            <span title={quiz.name} className={Styles.quizName}>{quiz.name}</span>
                        )}
                    </div>

                    <div className={Styles.cardActions}>
                        <div className={Styles.quizDetail}>
                            Assessments: {quiz.assessment_count}
                        </div>

                        {!isEditing && (
                            <button
                                className={Styles.descriptionToggle}
                                onClick={handleToggleDescription}
                                disabled={isAnyCardEditing}
                            >
                                {isExpanded ? (
                                    <>
                                        <FaChevronUp size={16} />
                                        <span>Hide description</span>
                                    </>
                                ) : (
                                    <>
                                        <FaChevronDown size={16} />
                                        <span>Show description</span>
                                    </>
                                )}
                            </button>
                        )}

                        {isEditing ? (
                            <div className={Styles.editActions}>
                                <Tooltip title="Save" arrow placement='bottom'>
                                    <span>
                                        <FeatherIcon
                                            icon="check"
                                            size={16}
                                            color="green"
                                            onClick={handleSave}
                                        />
                                    </span>
                                </Tooltip>
                                <Tooltip title="Cancel" arrow placement='bottom'>
                                    <span>
                                        <FeatherIcon
                                            icon="x"
                                            size={16}
                                            color="red"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCancel(quiz);
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                        ) : (
                            <Tooltip title="Edit" arrow placement='bottom'>
                                <span>
                                    <FeatherIcon
                                        icon="edit-2"
                                        size={16}
                                        color={isAnyCardEditing ? '#a0a0a0' : ''}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isAnyCardEditing) {
                                                onEdit(quiz.id);
                                            }
                                        }}
                                    />
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>

                <Collapse in={isExpanded || isEditing} timeout="auto">
                    <div className={Styles.descriptionContainer} ref={descriptionRef}>
                        {isEditing ? (
                            <TextField
                                value={editData.description}
                                onChange={handleInputChange('description')}
                                placeholder="Enter description"
                                multiline
                                rows={4}
                                fullWidth
                                error={!!errors.description}
                                helperText={errors.description}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white'
                                    }
                                }}
                            />
                        ) : (
                            <div className={Styles.descriptionContent}>
                                <h4>Description</h4>
                                {quiz.description ? (
                                    <p>{quiz.description}</p>
                                ) : (
                                    <p className={Styles.noDescription}>No description available</p>
                                )}
                            </div>
                        )}
                    </div>
                </Collapse>
            </div>
        </div>
    );
};

export function DiyQuiz() {
    const viewContext = useContext(ViewContext);
    const [quizzes, setQuizzes] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [newQuiz, setNewQuiz] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState({
        sortBy: 'created_at',
        sortOrder: 'desc',
    });
    const [paginationData, setPaginationData] = useState(null)
    const [pageNumber, setPageNumber] = useState(1)
    const [loading, setLoading] = useState(false);
    const [isAnyCardEditing, setIsAnyCardEditing] = useState(false);

    const sortOptions = useMemo(() => [
        { label: 'Newest First', value: { sortBy: 'created_at', sortOrder: 'desc' } },
        { label: 'Oldest First', value: { sortBy: 'created_at', sortOrder: 'asc' } },
        { label: 'Name (A-Z)', value: { sortBy: 'name', sortOrder: 'asc' } },
        { label: 'Name (Z-A)', value: { sortBy: 'name', sortOrder: 'desc' } },
    ], []);

    const isEditing = useMemo(() => {
        return editingId !== null || newQuiz !== null;
    }, [editingId, newQuiz]);

    useEffect(() => {
        setIsAnyCardEditing(editingId !== null || newQuiz !== null);
    }, [editingId, newQuiz]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const pageURL = `/api/diy-quiz?pageNumber=${pageNumber}&sortBy=${sortBy.sortBy}&sortOrder=${sortBy.sortOrder}`
                const response = await axios.get(pageURL);
                setPaginationData(response.data?.data?.pagination)
                setQuizzes(response.data?.data?.diyQuizData)
                setLoading(false)
            } catch (error) {
                console.error('Error fetching data:', error);
                viewContext.notification.show('Error Fetching Quizzes', 'error');
                setLoading(false)
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, sortBy.sortBy, sortBy.sortOrder])

    const handlePageChange = (event, value) => {
        setNewQuiz(null)
        setEditingId(null)
        setPageNumber(value);
        setSearchQuery('');
    };

    const handleAddNew = () => {
        setNewQuiz({
            id: 'temp-' + Date.now(),
            name: '',
            description: '',
            isNew: true,
            assessmentsCount: 0,
        });
        setSearchQuery('');
        setPageNumber(1);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        const pageUrl = query
            ? `/api/diy-quiz?search=${query}`
            : `/api/diy-quiz?pageNumber=${pageNumber}&sortBy=${sortBy.sortBy}&sortOrder=${sortBy.sortOrder}`;
        axios.get(pageUrl)
            .then(response => {
                // value !== '' ? setCancelSearch(true) : setCancelSearch(false);
                setQuizzes(response.data.data.diyQuizData)
            })
            .catch(error => {
                console.error('Error:', error);
                viewContext.notification.show(error.message, 'error');
            });
    }

    // Function to save quiz (both new and edited)
    const handleSave = async (quiz) => {
        try {
            // Trim the input values before saving
            const trimmedData = {
                name: quiz.name.trim(),
                description: quiz.description.trim()
            };

            if (quiz.isNew) {
                const formData = {
                    name: trimmedData.name,
                    description: trimmedData.description
                };

                const response = await axios.post('/api/diy-quiz', formData, {
                    headers: {
                        accept: 'application/json',
                    },
                });

                const newData = {
                    id: response.data.data.id,
                    name: response.data.data.name,
                    description: response.data.data.description
                };

                setQuizzes([...quizzes, newData]);
                viewContext.notification.show('Quiz created successfully', 'success');
                setNewQuiz(null);
            } else {
                const response = await axios.patch(
                    `/api/diy-quiz/${quiz.id}`,
                    trimmedData,
                    {
                        headers: {
                            accept: 'application/json',
                            'Content-Type': 'application/json'
                        }
                    }
                );

                const updatedQuiz = {
                    id: response.data.data.id,
                    name: response.data.data.name,
                    description: response.data.data.description,
                };

                setQuizzes(quizzes.map(q => q.id === quiz.id ? updatedQuiz : q));
                viewContext.notification.show('Quiz updated successfully', 'success');
                setEditingId(null);
            }
        } catch (error) {
            console.error('Failed to save quiz:', error);
            viewContext.notification.show(
                error.response?.data?.message || 'Failed to save quiz',
                'error'
            );
        }
    };

    return (
        <div className={Styles.ancestorContainer}>
            <div className={Styles.headerContainer}>
                <BreadCrumbs
                    onClickPaths={['']}
                    crumbs={["Your DIY Quizzes"]}
                />
                <AivagamButton
                    buttonText={
                        <span className={Styles.addButton}>
                            <AddCircle fontSize='small' />Add
                        </span>
                    }
                    type={"action"}
                    onClick={handleAddNew}
                    classStyles={{
                        width: '7em',
                        height: '3em',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                    }}
                />
            </div>
            <div className={Styles.filterContainer}>
                <SearchBar onSearch={handleSearch} disabled={isEditing} />
                <FormControl size="small" sx={{ width: '150px' }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                        value={JSON.stringify(sortBy)}
                        onChange={(e) => setSortBy(JSON.parse(e.target.value))}
                        label="Sort by"
                        disabled={isEditing}
                        sx={{
                            '& .Mui-disabled': {
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed'
                            }
                        }}
                    >
                        {sortOptions.map((option) => (
                            <MenuItem key={option.label} value={JSON.stringify(option.value)}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            {loading ? (
                <div className={Styles.quizList}>
                    {[1, 2, 3, 4].map((index) => (
                        <Skeleton
                            key={index}
                            variant="rectangle"
                            width="100%"
                            height={45}
                        />
                    ))}
                </div>
            ) : (
                <>
                    <div className={Styles.quizList}>
                        {newQuiz && (
                            <QuizCard
                                quiz={newQuiz}
                                onSave={handleSave}
                                onCancel={() => setNewQuiz(null)}
                                isEditing={true}
                                onEdit={setEditingId}
                                onToggleDescription={setExpandedId}
                                expandedId={expandedId}
                                isAnyCardEditing={isAnyCardEditing}
                            />
                        )}
                        {quizzes?.length > 0 ? (
                            quizzes.map(quiz => (
                                <QuizCard
                                    key={quiz.id}
                                    quiz={quiz}
                                    onSave={handleSave}
                                    onCancel={() => setEditingId(null)}
                                    isEditing={editingId === quiz.id}
                                    onEdit={setEditingId}
                                    onToggleDescription={setExpandedId}
                                    expandedId={expandedId}
                                    isAnyCardEditing={isAnyCardEditing}
                                />
                            ))
                        ) : (
                            !newQuiz && (
                                searchQuery ? (
                                    <div className={Styles.noResults}>
                                        <p>No quizzes found matching your search.</p>
                                        <p>Try different keywords or clear your search to see all quizzes.</p>
                                    </div>
                                ) : (
                                    <div className={Styles.noResults}>
                                        <p>You haven't created any quizzes yet.</p>
                                        <p>Click the "Add" button above to create your first quiz.</p>
                                    </div>
                                )
                            )
                        )}
                    </div>

                </>
            )}
            {/* Pagination */}
            <div className={Styles.paginationContainer}>
                <Pagination
                    color="primary"
                    page={pageNumber}
                    count={paginationData?.totalPages || 1}
                    size="small"
                    sx={{
                        maxWidth: "50%",
                        width: "fit-content",
                        marginTop: "0.5em",
                    }}
                    onChange={handlePageChange}
                />
            </div>
        </div >
    );
}